<template>
  <v-container fluid class="catalog-page pa-0">
    <v-dialog
      v-model="catalogDialog"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card class="catalog-shell">
        <div class="catalog-topbar">
          <div>
            <div class="catalog-title">
              Start Order
              <span v-if="customerInfo && customerInfo.businessName">
                · {{ customerInfo.businessName }}
              </span>
            </div>
            <div class="catalog-subtitle">
              {{ Order.OrderDetails.length }} item rows · {{ totalQty }} qty ·
              {{ orderTotal | currency }}

              <span v-if="autosaveLoading"> · Saving...</span>
              <span v-else-if="autosaveError" class="red--text">
                · Not saved</span
              >
              <span v-else-if="lastSavedAt">
                · Saved {{ lastSavedAt | moment("hh:mm A") }}
              </span>
            </div>
          </div>

          <div class="catalog-actions">
            <v-btn rounded outlined @click="closeCatalog"> Close </v-btn>

            <v-btn
              :color="buttonColor"
              :dark="darkMode"
              rounded
              :loading="saving"
              :disabled="Order.OrderDetails.length === 0"
              @click="saveOrder"
            >
              {{ orderTotal | currency }} Save Order
            </v-btn>
          </div>
        </div>

        <div class="catalog-filter-bar compact-filter-bar">
          <v-text-field
            v-model="search"
            outlined
            dense
            clearable
            hide-details
            prepend-inner-icon="mdi-magnify"
            label="Search products"
            class="catalog-search"
          />
        </div>

        <div class="category-tabs" v-if="Categories.length">
          <v-slide-group show-arrows>
            <v-slide-item v-for="category in Categories" :key="category.id">
              <v-btn
                small
                rounded
                class="mr-2"
                :disabled="loading"
                :color="selectedCategory === category.id ? buttonColor : ''"
                :dark="selectedCategory === category.id ? darkMode : false"
                :outlined="selectedCategory !== category.id"
                :class="{ 'category-loading-disabled': loading }"
                @click="selectCategory(category)"
              >
                {{ category.name }}
              </v-btn>
            </v-slide-item>
          </v-slide-group>
        </div>

        <div class="catalog-body">
          <div class="product-area" ref="productArea">
            <div v-if="loading" class="catalog-loading-overlay">
              <v-progress-circular indeterminate color="primary" size="42" />
              <div class="loading-title">Loading products...</div>
              <div class="loading-subtitle">
                Getting items for this category
              </div>
            </div>

            <div
              v-for="department in groupedCatalogProducts"
              :key="department.name"
              class="department-group"
            >
              <div class="department-title">
                {{ department.name }}
                <span>{{ department.count }} items</span>
              </div>

              <v-row dense>
                <v-col
                  v-for="product in department.products"
                  :key="product.id"
                  cols="6"
                  sm="4"
                  md="2"
                  lg="2"
                >
                  <CatalogProductCard
                    :product="product"
                    :qty="getQty(product)"
                    :shouldShowStock="true"
                    :buttonColor="buttonColor"
                    :darkMode="darkMode"
                    @add="add"
                    @minus="minus"
                    @open-qty="openQty"
                    @image-click="openImage"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>

        <div class="floating-cart-bar" v-if="Order.OrderDetails.length">
          <div>
            <div class="floating-cart-label">Cart</div>
            <div class="floating-cart-total">
              {{ totalQty }} items · {{ orderTotal | currency }}
            </div>
          </div>

          <v-btn
            :color="buttonColor"
            :dark="darkMode"
            rounded
            @click="cartDrawer = true"
          >
            <v-icon left small>mdi-cart-outline</v-icon>
            Review Cart
          </v-btn>
        </div>

        <CatalogCartDrawer
          v-model="cartDrawer"
          :items="Order.OrderDetails"
          :totalQty="totalQty"
          :total="orderTotal"
          :saving="saving"
          :buttonColor="buttonColor"
          :darkMode="darkMode"
          @add="add"
          @minus="minus"
          @open-qty="openQty"
          @remove="removeItem"
          @save="saveOrder"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="imageDialog" max-width="760px">
      <v-card v-if="imageProduct" class="rounded-xl">
        <div class="dialog-header">
          <div class="dialog-title">{{ imageProduct.name }}</div>

          <v-btn icon small @click="closeImage">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4">
          <v-img
            v-if="imageProduct.imageUrl"
            :src="imageProduct.imageUrl"
            contain
            max-height="620"
          />

          <v-img
            v-else
            src="@/assets/images/photoComingSoon.png"
            contain
            max-height="420"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="qtyDialog" max-width="380px">
      <v-card class="qty-pad-card">
        <div class="qty-pad-header">
          <div>
            <div class="qty-pad-title">Enter Quantity</div>
            <div class="qty-pad-product">
              {{ currentProduct ? currentProduct.name : "" }}
            </div>
          </div>

          <v-btn icon small @click="closeQtyPad">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="qty-pad-display">
          {{ manualQtyDisplay || "0" }}
        </div>

        <div class="qty-pad-grid">
          <v-btn
            v-for="number in keypadNumbers"
            :key="number"
            class="qty-pad-btn"
            depressed
            @click="pressQtyNumber(number)"
          >
            {{ number }}
          </v-btn>

          <v-btn class="qty-pad-btn danger" depressed @click="clearQty">
            Clear
          </v-btn>

          <v-btn class="qty-pad-btn" depressed @click="pressQtyNumber(0)">
            0
          </v-btn>

          <v-btn class="qty-pad-btn danger" depressed @click="backspaceQty">
            <v-icon>mdi-backspace-outline</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-actions class="qty-pad-actions">
          <v-btn text @click="closeQtyPad">Cancel</v-btn>
          <v-spacer />
          <v-btn
            :color="buttonColor"
            :dark="darkMode"
            rounded
            large
            @click="saveQty"
          >
            Apply Qty
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import CatalogProductCard from "@/modules/Order/components/PublicCatalog/PublicCatalogProductCard.vue";
import CatalogCartDrawer from "@/modules/Order/components/PublicCatalog/PublicCatalogCartDrawer.vue";
import categoryService from "@/modules/Product/Category/service.js";
import orderService from "@/modules/Order/components/PublicCatalog/service.js";
import { mapGetters } from "vuex";
import Hashids from "hashids";

const hashids = new Hashids();
export default {
  name: "catalog-order-create",

  components: {
    CatalogProductCard,
    CatalogCartDrawer,
  },

  data() {
    return {
      code: null,
      business: null,
      catalogDialog: true,
      customerInfo: null,

      draftOrderId: null,
      autosaveTimer: null,
      autosaveLoading: false,
      autosaveError: false,
      lastSavedAt: null,

      productCache: {},
      qtyMap: {},
      cartDrawer: false,

      imageDialog: false,
      imageProduct: null,

      loading: false,
      saving: false,

      search: "",
      searchTimer: null,

      Categories: [],
      Products: [],
      productLimit: 150,
      productOffset: 0,
      hasMoreProducts: false,
      selectedCategory: null,

      qtyDialog: false,
      currentProduct: null,
      manualQty: 0,
      manualQtyDisplay: "",
      keypadNumbers: [1, 2, 3, 4, 5, 6, 7, 8, 9],

      Order: {
        date: this.$moment().toISOString(),
        note: null,
        OrderDetails: [],
      },
    };
  },
  computed: {
    ...mapGetters("global", ["currentUser", "currentBusiness"]),
    buttonColor() {
      if (
        this.business &&
        this.business.metadata &&
        this.business.metadata.remoteOrderBackgroundColor
      ) {
        return this.business.metadata.remoteOrderBackgroundColor;
      }

      return "#F0C316";
    },

    darkMode() {
      return !!(
        this.business &&
        this.business.metadata &&
        this.business.metadata.remoteOrderDarkMode
      );
    },
    collectionId() {
      return this.$route.query.collectionId || null;
    },
    groupedCatalogProducts() {
      const departmentMap = {};

      this.Products.forEach((product) => {
        const departmentName =
          product.Department && product.Department.name
            ? product.Department.name
            : "Unassigned";

        if (!departmentMap[departmentName]) {
          departmentMap[departmentName] = [];
        }

        departmentMap[departmentName].push(product);
      });

      return Object.keys(departmentMap)
        .sort((a, b) => a.localeCompare(b))
        .map((departmentName) => {
          const products = departmentMap[departmentName].sort((a, b) => {
            const brandA = a.Brand && a.Brand.name ? a.Brand.name : "No Brand";
            const brandB = b.Brand && b.Brand.name ? b.Brand.name : "No Brand";

            const brandCompare = brandA.localeCompare(brandB);

            if (brandCompare !== 0) {
              return brandCompare;
            }

            return String(a.name || "").localeCompare(String(b.name || ""));
          });

          return {
            name: departmentName,
            count: products.length,
            products,
          };
        });
    },
    totalQty() {
      return this.Order.OrderDetails.reduce((sum, row) => {
        return sum + Number(row.qty || 0);
      }, 0);
    },

    orderTotal() {
      return this.Order.OrderDetails.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);
    },
  },

  watch: {
    search() {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(async () => {
        this.productOffset = 0;
        this.hasMoreProducts = false;
        this.Products = [];

        this.scrollProductTop();

        await this.getProducts(true);

        this.scrollProductTop();
      }, 300);
    },
  },

  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
    }
  },
  async created() {
    this.code = this.decodePublicCode();

    await this.loadPublicBusiness();

    const savedInfo =
      localStorage.getItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer_${this.code}`
      ) || localStorage.getItem("publicOrderCustomerInfo");

    if (savedInfo) {
      try {
        this.customerInfo = JSON.parse(savedInfo);
      } catch (e) {
        this.customerInfo = null;
      }
    }

    await this.loadOrder();
    await this.getCategories();
    await this.getProducts();
  },

  methods: {
    async loadPublicBusiness() {
      try {
        const response = await orderService.getBusinessInfo(this.code);
        this.business = response.data.business;
      } catch (error) {
        console.error("load public business failed", error);
        this.business = null;
      }
    },
    async hasVirtualCategoryProducts(type) {
      const where = {
        key: this.code,
        hideParent: true,
        remoteOrder: true,
        limit: 1,
        offset: 0,
      };

      if (this.collectionId) {
        where.CollectionId = this.collectionId;
      }

      if (
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.showOutOfStockItems
      ) {
        where.hideZero = false;
      } else {
        where.hideZero = true;
      }

      if (type === "new_product") {
        where.isNew = true;
      }

      if (type === "special_prices") {
        where.isSpecial = true;
      }

      const response = await orderService.getPublicProduct(where);
      return (response.data || []).length > 0;
    },
    async loadOrder() {
      const orderId = this.$route.params.orderId;

      if (!orderId) return;

      try {
        const response = await orderService.getOrderByIdPublic(
          orderId,
          this.code
        );
        const order = response.data;

        this.draftOrderId = order.id;

        this.Order = {
          date: order.date || this.$moment().toISOString(),
          note: order.note || null,
          OrderDetails: [],
        };

        this.qtyMap = {};

        if (order.metadata && order.metadata.publicCustomer) {
          this.customerInfo = order.metadata.publicCustomer;
        }

        (order.OrderDetails || []).forEach((detail) => {
          const product = detail.Product || {};

          const row = {
            ProductId: detail.ProductId,
            Product: {
              ...product,
              id: detail.ProductId,
              name: detail.name || product.name,
              price: Number(detail.price || 0),
              imageUrl: detail.imageUrl || product.imageUrl,
              unitCountInBox: detail.unitCountInBox,
            },
            name: detail.name || product.name,
            price: Number(detail.price || 0),
            qty: Number(detail.qty || 0),
            boxQty: Number(detail.boxQty || detail.qty || 0),
            unitCountInBox: detail.unitCountInBox,
            imageUrl: detail.imageUrl || product.imageUrl,
            total: Number(
              detail.total ||
                detail.totalPrice ||
                Number(detail.qty || 0) * Number(detail.price || 0)
            ),
          };

          this.Order.OrderDetails.push(row);
          this.$set(this.qtyMap, detail.ProductId, row.qty);
        });

        this.lastSavedAt = order.updatedAt || this.$moment().toISOString();
      } catch (error) {
        console.error("load public order failed", error);
        this.$swal("Order", "Unable to open this order", "error");
        this.$router.push(`/p/o/${this.$route.params.id}`);
      }
    },

    getPublicDeviceKey() {
      const key = `${process.env.VUE_APP_STORAGE_PREFIX}publicOrderDeviceKey_${this.code}`;
      let value = localStorage.getItem(key);

      if (!value) {
        value = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(key, value);
      }

      return value;
    },
    async startPublicOrder(customerInfo) {
      localStorage.removeItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}publicDraftOrderId_${this.code}`
      );

      this.draftOrderId = null;

      this.customerInfo = customerInfo;

      localStorage.setItem(
        "publicOrderCustomerInfo",
        JSON.stringify(customerInfo)
      );

      this.catalogDialog = false;
      this.customerDialog = true;

      await this.getCategories();
      await this.getProducts();
    },
    decodePublicCode() {
      const routeId = this.$route.params.id;

      if (!routeId) return null;

      const hashPart = routeId.includes("-") ? routeId.split("-")[0] : routeId;
      const decoded = hashids.decode(hashPart);

      return decoded && decoded.length ? decoded[0].toString() : null;
    },
    scrollProductTop() {
      this.$nextTick(() => {
        if (this.$refs.productArea) {
          this.$refs.productArea.scrollTop = 0;
        }
      });
    },
    queueAutosave() {
      if (!this.customerInfo) return;
      if (!this.Order.OrderDetails.length) return;

      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer);
      }

      this.autosaveTimer = setTimeout(() => {
        this.autosaveDraft();
      }, 1500);
    },
    buildPublicOrderPayload(status = "draft") {
      return {
        date: this.Order.date || this.$moment().toISOString(),
        note: this.Order.note || null,
        status,
        source: "public",
        metadata: {
          publicCustomer: this.customerInfo,
          deviceKey: this.getPublicDeviceKey(),
        },
        OrderDetails: this.Order.OrderDetails.map((row) => {
          return {
            ProductId: row.ProductId,
            name: row.name,
            price: Number(row.price || 0),
            qty: Number(row.qty || 0),
            boxQty: Number(row.boxQty || row.qty || 0),
            unitCountInBox: row.unitCountInBox,
            imageUrl: row.imageUrl,
            total: Number(row.total || 0),
          };
        }),
      };
    },
    async autosaveDraft() {
      if (!this.Order.OrderDetails.length) return;
      if (!this.customerInfo) return;

      this.autosaveLoading = true;
      this.autosaveError = false;

      try {
        const response = await orderService.publicAutosaveDraft(this.code, {
          id: this.draftOrderId,
          ...this.buildPublicOrderPayload("draft"),
        });

        this.draftOrderId = response.data.id;
        this.lastSavedAt = this.$moment().toISOString();
      } catch (error) {
        console.error("public autosave failed", error);
        this.autosaveError = true;
      } finally {
        this.autosaveLoading = false;
      }
    },
    getProductCacheKey() {
      return [
        String(this.collectionId || "no_collection"),
        String(this.selectedCategory || "all"),
        String(this.search || ""),
      ].join("|");
    },
    openImage(product) {
      this.imageProduct = product;
      this.$nextTick(() => {
        this.imageDialog = true;
      });
    },

    closeImage() {
      this.imageDialog = false;

      this.$nextTick(() => {
        this.imageProduct = null;
      });
    },

    async getCategories() {
      try {
        const where = {
          key: this.code,
          remoteOrder: true,
        };

        if (this.collectionId) {
          where.CollectionId = this.collectionId;
        }

        const response = await orderService.getAllCategory(where);

        const categories = (response.data || []).sort((a, b) => {
          return String(a.name || "").localeCompare(String(b.name || ""));
        });

        const hasNewProductCategory = categories.some((row) => {
          return String(row.name || "").toLowerCase() === "new product";
        });

        const hasSpecialPricesCategory = categories.some((row) => {
          return String(row.name || "").toLowerCase() === "special prices";
        });

        const canShowNewProduct = await this.hasVirtualCategoryProducts(
          "new_product"
        );
        const canShowSpecialPrices = await this.hasVirtualCategoryProducts(
          "special_prices"
        );

        if (!hasSpecialPricesCategory && canShowSpecialPrices) {
          categories.unshift({
            id: "special_prices",
            name: "Special Prices",
          });
        }

        if (!hasNewProductCategory && canShowNewProduct) {
          categories.unshift({
            id: "new_product",
            name: "New Product",
          });
        }

        this.Categories = categories;

        if (this.Categories.length) {
          this.selectedCategory = this.Categories[0].id;
        }
      } catch (error) {
        console.error("get categories failed", error);
        this.Categories = [];
      }
    },

    async getProducts(forceRefresh = false) {
      const cacheKey = this.getProductCacheKey();

      if (!forceRefresh && this.productCache[cacheKey]) {
        this.Products = this.productCache[cacheKey].map((product) => {
          return {
            ...product,
            orderQuantity: this.getQty(product),
          };
        });
        return;
      }

      this.loading = true;

      try {
        const selectedCategoryObj = this.Categories.find((row) => {
          return String(row.id) === String(this.selectedCategory);
        });

        const selectedName =
          selectedCategoryObj && selectedCategoryObj.name
            ? String(selectedCategoryObj.name).toLowerCase().trim()
            : "";

        const where = {
          key: this.code,
          hideParent: true,
          remoteOrder: true,
          limit: this.productLimit,
          offset: this.productOffset,
          search: this.search,
          sortBy: ["Department.name", "Brand.name", "name"],
          sortDesc: [false, false, false],
        };

        if (this.collectionId) {
          where.CollectionId = this.collectionId;
        }

        if (
          this.currentBusiness &&
          this.currentBusiness.metadata &&
          this.currentBusiness.metadata.showOutOfStockItems
        ) {
          where.hideZero = false;
        } else {
          where.hideZero = true;
        }

        const hasSearch = String(this.search || "").trim().length > 0;

        if (!hasSearch) {
          if (
            this.selectedCategory === "special_prices" ||
            selectedName === "special prices" ||
            selectedName === "special price"
          ) {
            where.isSpecial = true;
          } else if (
            this.selectedCategory === "new_product" ||
            selectedName === "new product" ||
            selectedName === "new products"
          ) {
            where.isNew = true;
          } else if (this.selectedCategory) {
            where.CategoryId = this.selectedCategory;
          }
        }

        const response = await orderService.getPublicProduct({
          ...where,
          key: this.code,
        });
        let products = response.data || [];

        products = products.map((product) => {
          return {
            ...product,
            orderQuantity: this.getQty(product),
          };
        });

        this.Products = products;

        this.$set(this.productCache, cacheKey, this.Products);
      } catch (error) {
        console.error("get products failed", error);
        this.Products = [];
      } finally {
        this.loading = false;
      }
    },

    async selectCategory(category) {
      if (this.loading) return;

      this.selectedCategory = category.id;
      this.productOffset = 0;
      this.hasMoreProducts = false;
      this.Products = [];

      this.scrollProductTop();

      await this.getProducts(true);

      this.scrollProductTop();
    },

    getQty(product) {
      return Number(this.qtyMap[product.id] || 0);
    },

    add(product) {
      const currentQty = this.getQty(product);
      this.setQty(product, currentQty + 1);
    },

    minus(product) {
      const currentQty = this.getQty(product);
      if (currentQty <= 0) return;
      this.setQty(product, currentQty - 1);
    },

    openQty(product) {
      this.currentProduct = product;

      const currentQty = this.getQty(product);

      this.manualQty = currentQty || 0;
      this.manualQtyDisplay = currentQty ? String(currentQty) : "";

      this.qtyDialog = true;
    },

    pressQtyNumber(number) {
      const value = String(number);

      if (this.manualQtyDisplay.length >= 5) return;

      if (this.manualQtyDisplay === "0") {
        this.manualQtyDisplay = value;
      } else {
        this.manualQtyDisplay += value;
      }

      this.manualQty = Number(this.manualQtyDisplay || 0);
    },

    backspaceQty() {
      this.manualQtyDisplay = this.manualQtyDisplay.slice(0, -1);
      this.manualQty = Number(this.manualQtyDisplay || 0);
    },

    clearQty() {
      this.manualQtyDisplay = "";
      this.manualQty = 0;
    },

    closeQtyPad() {
      this.qtyDialog = false;
      this.currentProduct = null;
      this.manualQty = 0;
      this.manualQtyDisplay = "";
    },

    saveQty() {
      if (!this.currentProduct) return;

      const qty = Number(this.manualQtyDisplay || 0);

      this.setQty(this.currentProduct, qty);

      this.closeQtyPad();
    },
    setQty(product, qty) {
      qty = Number(qty || 0);

      this.$set(this.qtyMap, product.id, qty);
      this.$set(product, "orderQuantity", qty);

      const index = this.Order.OrderDetails.findIndex((row) => {
        return Number(row.ProductId) === Number(product.id);
      });

      if (qty <= 0) {
        if (index !== -1) {
          this.Order.OrderDetails.splice(index, 1);
        }

        this.queueAutosave();
        return;
      }

      const price = Number(product.price || 0);
      const total = Number((price * qty).toFixed(2));

      const detail = {
        ProductId: product.id,
        Product: product,
        name: product.name,
        price,
        qty,
        boxQty: qty,
        unitCountInBox: product.unitCountInBox,
        imageUrl: product.imageUrl,
        total,
      };

      if (index === -1) {
        this.Order.OrderDetails.push(detail);
      } else {
        this.$set(this.Order.OrderDetails, index, detail);
      }

      this.queueAutosave();
    },

    updateProductQty(productId, qty) {
      this.$set(this.qtyMap, productId, qty);

      const product = this.Products.find((row) => {
        return Number(row.id) === Number(productId);
      });

      if (product) {
        this.$set(product, "orderQuantity", qty);
      }
    },

    removeItem(index) {
      const detail = this.Order.OrderDetails[index];
      this.Order.OrderDetails.splice(index, 1);

      if (detail) {
        this.updateProductQty(detail.ProductId, 0);
      }
      this.queueAutosave();
    },

    async saveOrder() {
      if (!this.Order.OrderDetails.length) return;

      this.saving = true;

      try {
        if (this.autosaveTimer) {
          clearTimeout(this.autosaveTimer);
          this.autosaveTimer = null;
        }

        await this.autosaveDraft();

        await orderService.publicSubmitDraft(this.code, this.draftOrderId);
        localStorage.removeItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}publicDraftOrderId_${this.code}`
        );

        this.draftOrderId = null;

        this.$swal("Order Submitted", "Your order was submitted.", "success");
        this.$router.push({
          path: `/p/o/${this.$route.params.id}`,
          query: this.collectionId ? { collectionId: this.collectionId } : {},
        });
      } catch (error) {
        console.error("submit public order failed", error);
        this.$swal("Order", "Unable to submit order", "error");
      } finally {
        this.saving = false;
      }
    },
    
    closeCatalog() {
      this.$swal({
        title: "Close catalog?",
        text: "Your draft order will stay saved.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Close",
        cancelButtonText: "Keep working",
      }).then((result) => {
        if (result.value) {
          this.$router.push({
            path: `/p/o/${this.$route.params.id}`,
            query: this.collectionId ? { collectionId: this.collectionId } : {},
          });
        }
      });
    },

    cancel() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.catalog-page {
  background: #f8fafc;
  min-height: 100%;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.dialog-actions {
  padding: 12px 16px;
}

.field-label {
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  margin-bottom: 6px;
}

.catalog-shell {
  background: #f8fafc;
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.catalog-topbar {
  height: 54px;
  min-height: 54px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.catalog-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.1;
}

.catalog-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.catalog-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.catalog-actions .v-btn {
  height: 34px !important;
}

.compact-filter-bar {
  min-height: 54px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 12px;
  display: flex;
  align-items: center;
}

.clickable-image {
  cursor: zoom-in;
}

.catalog-search {
  max-width: 420px;
}

.category-tabs {
  min-height: 48px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 12px;
}

.catalog-body {
  flex: 1;
  min-height: 0;
  padding: 10px 8px 86px;
  overflow: hidden;
}

.product-area {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;
}

.department-title {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #f8fafc;
  padding: 8px 4px;
  font-size: 17px;
  font-weight: 900;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.department-title span {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.brand-title {
  font-size: 13px;
  font-weight: 900;
  color: #334155;
  background: #eaf0ff;
  border: 1px solid #dbe4ff;
  border-radius: 10px;
  padding: 7px 10px;
  margin: 8px 0;
}

.compact-product-card {
  border-radius: 14px;
  height: 100%;
  overflow: hidden;
}

.product-image,
.compact-no-image {
  height: 92px !important;
  background: #f8fafc;
}

.compact-product-content {
  padding: 8px !important;
}

.compact-product-name {
  font-size: 12px;
  font-weight: 900;
  line-height: 1.25;
  color: #0f172a;
  min-height: 46px;
  max-height: 46px;
  overflow: hidden;
}

.product-price-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-price {
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
}

.touch-qty-row {
  margin-top: 8px;
  display: grid;
  grid-template-columns: 44px 1fr 44px;
  gap: 6px;
  align-items: center;
}

.touch-qty-btn {
  height: 40px !important;
  min-width: 40px !important;
  border-radius: 12px !important;
}

.minus-btn {
  background: #fee2e2 !important;
  color: #dc2626 !important;
}

.plus-btn {
  background: #facc15 !important;
  color: #0f172a !important;
}

.touch-qty-value {
  height: 40px;
  border: 1px solid #0f172a;
  border-radius: 12px;
  background: #ffffff;
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

.floating-cart-bar {
  position: fixed;
  left: 10px;
  right: 10px;
  bottom: 10px;
  z-index: 20;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.18);
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.floating-cart-total {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

@media (max-width: 960px) {
  .catalog-title {
    font-size: 16px;
  }

  .catalog-subtitle {
    font-size: 11px;
  }

  .catalog-search {
    max-width: 100%;
  }

  .product-image,
  .compact-no-image {
    height: 86px !important;
  }

  .touch-qty-row {
    grid-template-columns: 42px 1fr 42px;
  }

  .touch-qty-btn,
  .touch-qty-value {
    height: 38px !important;
  }
}

@media (max-width: 600px) {
  .catalog-topbar {
    height: auto;
    min-height: 58px;
    align-items: flex-start;
  }

  .catalog-actions {
    flex-shrink: 0;
  }

  .catalog-actions .v-btn:first-child {
    display: none;
  }

  .compact-filter-bar {
    padding: 8px;
  }

  .category-tabs {
    padding: 7px 8px;
  }

  .catalog-body {
    padding: 8px 6px 84px;
  }

  .compact-product-name {
    font-size: 11px;
  }
}

.catalog-filter-bar {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.catalog-search {
  max-width: 480px;
}

.category-tabs {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 16px;
}

.catalog-body {
  flex: 1;
  min-height: 0;
  padding: 12px;
  padding-bottom: 92px;
}

.product-area {
  position: relative;
  overflow: auto;
  height: 100%;
}

.catalog-loading-overlay {
  position: sticky;
  top: 0;
  z-index: 8;
  min-height: 180px;
  background: rgba(248, 250, 252, 0.92);
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  backdrop-filter: blur(3px);
  margin-bottom: 12px;
}

.loading-title {
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
  margin-top: 10px;
}

.loading-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.category-loading-disabled {
  opacity: 0.45;
}

.department-group {
  margin-bottom: 20px;
}

.department-title {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8fafc;
  padding: 10px 4px;
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.department-title span {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.brand-group {
  margin-bottom: 14px;
}

.brand-title {
  font-size: 13px;
  font-weight: 900;
  color: #475569;
  background: #eef2ff;
  border: 1px solid #dbe4ff;
  border-radius: 12px;
  padding: 7px 10px;
  margin-bottom: 8px;
}

.compact-product-card {
  border-radius: 16px;
}

.compact-no-image {
  height: 105px;
}

.compact-product-content {
  padding: 9px !important;
}

.compact-product-name {
  font-size: 12px;
  line-height: 1.25;
  min-height: 46px;
}

.compact-qty-row {
  gap: 6px;
}

.floating-cart-bar {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 20;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.18);
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.floating-cart-label {
  font-size: 11px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
}

.floating-cart-total {
  font-size: 17px;
  font-weight: 900;
  color: #0f172a;
}

@media (max-width: 600px) {
  .catalog-body {
    padding: 8px;
    padding-bottom: 96px;
  }

  .department-title {
    font-size: 16px;
  }

  .brand-title {
    font-size: 12px;
  }

  .floating-cart-bar {
    left: 8px;
    right: 8px;
    bottom: 8px;
  }

  .floating-cart-bar .v-btn {
    min-width: 120px;
  }

  .floating-cart-total {
    font-size: 15px;
  }
}

.cart-title {
  padding: 14px;
  font-size: 18px;
  font-weight: 900;
  border-bottom: 1px solid #e2e8f0;
}

.product-image {
  background: #f8fafc;
}

.no-image {
  height: 150px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.product-content {
  padding: 12px;
}

.product-name {
  font-size: 14px;
  font-weight: 900;
  color: #0f172a;
  min-height: 42px;
}

.product-meta {
  font-size: 12px;
  color: #64748b;
  min-height: 18px;
}

.clickable-image {
  cursor: zoom-in;
}

.image-preview-card {
  overflow: hidden;
}

.image-preview-header {
  height: 52px;
  padding: 0 14px 0 18px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.image-preview-title {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.2;
}

.product-price-row {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
}

.product-price {
  font-size: 16px;
  font-weight: 900;
  color: #0f172a;
}

.qty-btn {
  min-width: 64px;
}

.table-product {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-qty-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.centered-input input {
  text-align: center;
}

.qty-pad-card {
  border-radius: 16px;
  overflow: hidden;
}

.qty-pad-header {
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.qty-pad-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.qty-pad-product {
  font-size: 15px;
  color: #334155;
  line-height: 1.35;
  margin-top: 4px;
}

.qty-pad-display {
  height: 58px;
  padding: 8px 18px;
  font-size: 34px;
  font-weight: 900;
  color: #0f172a;
  text-align: right;
  border-bottom: 1px solid #e2e8f0;
}

.qty-pad-grid {
  padding: 14px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.qty-pad-btn {
  height: 58px !important;
  min-width: 0 !important;
  width: 100%;
  border-radius: 14px !important;
  font-size: 24px !important;
  font-weight: 900 !important;
  background: #f8fafc !important;
  color: #0f172a !important;
}

.qty-pad-btn.danger {
  font-size: 15px !important;
  letter-spacing: 1px;
}

.qty-pad-actions {
  padding: 12px 16px;
}

.qty-pad-actions .v-btn {
  min-height: 44px;
}

@media (max-width: 600px) {
  .catalog-topbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .catalog-actions {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .catalog-actions .v-btn {
    width: 100%;
  }

  .catalog-filter-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .catalog-search {
    max-width: 100%;
  }
}
</style>

